import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Megaphone,
  Pin,
  Calendar,
  AlertCircle,
  Info,
  Clock,
  Search,
  Eye,
  X,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import type { Announcement } from '../BulletinBoard';

const categoryOptions = [
  { value: 'general', label: 'General', icon: Info, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'academic', label: 'Academic', icon: Megaphone, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'deadline', label: 'Deadline', icon: Clock, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'event', label: 'Event', icon: Calendar, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'urgent', label: 'Urgent', icon: AlertCircle, color: 'bg-red-100 text-red-700 border-red-200' },
];

const audienceOptions = [
  { value: 'public', label: 'Public (Everyone)' },
  { value: 'faculty', label: 'All Faculty' },
  { value: 'students', label: 'All Students' },
];

const defaultFormData = {
  title: '',
  content: '',
  category: 'general' as Announcement['category'],
  audience: 'public' as Announcement['audience'],
  isPinned: false,
  expiresAt: '',
};

export function AnnouncementManagement({ globalSemester, globalAcademicYear }: { globalSemester?: number; globalAcademicYear?: string }) {
  const { accessToken } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [previewAnnouncement, setPreviewAnnouncement] = useState<Announcement | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await api.getAnnouncements();
      setAnnouncements(data.announcements || []);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      setSubmitting(true);
      const token = accessToken || '';

      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        category: formData.category,
        audience: formData.audience,
        isPinned: formData.isPinned,
        expiresAt: formData.expiresAt || null,
      };

      if (editingAnnouncement) {
        await api.updateAnnouncement(editingAnnouncement.id, payload, token);
        toast.success('Announcement updated successfully');
      } else {
        await api.createAnnouncement(payload, token);
        toast.success('Announcement published successfully');
      }

      await fetchAnnouncements();
      handleDialogClose();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      category: announcement.category,
      audience: announcement.audience || 'public',
      isPinned: announcement.isPinned,
      expiresAt: announcement.expiresAt
        ? new Date(announcement.expiresAt).toISOString().slice(0, 16)
        : '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const token = accessToken || '';
      await api.deleteAnnouncement(id, token);
      toast.success('Announcement deleted');
      await fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingAnnouncement(null);
    setFormData(defaultFormData);
  };

  const getCategoryBadge = (category: string) => {
    const opt = categoryOptions.find((c) => c.value === category);
    if (!opt) return null;
    return (
      <Badge variant="outline" className={`text-xs ${opt.color}`}>
        {opt.label}
      </Badge>
    );
  };

  const getAudienceBadge = (audience?: string) => {
    const aud = audience || 'public';
    const config: Record<string, { label: string; className: string }> = {
      public: { label: 'Public', className: 'bg-green-100 text-green-700 border-green-200' },
      faculty: { label: 'All Faculty', className: 'bg-purple-100 text-purple-700 border-purple-200' },
      students: { label: 'All Students', className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    };
    const c = config[aud] || config.public;
    return (
      <Badge variant="outline" className={`text-xs ${c.className}`}>
        {c.label}
      </Badge>
    );
  };

  const filteredAnnouncements = announcements
    .filter((a) => {
      const matchesSearch =
        !searchQuery ||
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || a.category === filterCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.datePosted).getTime() - new Date(a.datePosted).getTime();
    });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-xl">Bulletin Board Management</CardTitle>
                <CardDescription>
                  Create and manage graduate school announcements displayed on the landing page
                  {globalSemester && globalAcademicYear && (
                    <span className="ml-1 font-medium">
                      — {globalSemester === 1 ? '1st Semester' : globalSemester === 2 ? '2nd Semester' : 'Summer'}, A.Y. {globalAcademicYear}
                    </span>
                  )}
                </CardDescription>
              </div>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) handleDialogClose(); else setIsDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white gap-2">
                  <Plus className="h-4 w-4" />
                  New Announcement
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingAnnouncement
                      ? 'Update the announcement details below.'
                      : 'Fill in the details to publish a new announcement on the bulletin board.'}
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      placeholder="Enter announcement title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <Label htmlFor="content">Content *</Label>
                    <Textarea
                      id="content"
                      placeholder="Enter announcement content..."
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={5}
                      required
                    />
                  </div>

                  {/* Category */}
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) =>
                        setFormData({ ...formData, category: v as Announcement['category'] })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="w-4 h-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Audience */}
                  <div className="space-y-2">
                    <Label>Audience</Label>
                    <Select
                      value={formData.audience}
                      onValueChange={(v) =>
                        setFormData({ ...formData, audience: v as Announcement['audience'] })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select audience" />
                      </SelectTrigger>
                      <SelectContent>
                        {audienceOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Choose who can see this announcement on the bulletin board.
                    </p>
                  </div>

                  {/* Pinned toggle */}
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <Label htmlFor="pinned" className="text-sm font-medium">
                        Pin Announcement
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Pinned announcements appear at the top of the bulletin board
                      </p>
                    </div>
                    <Switch
                      id="pinned"
                      checked={formData.isPinned}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, isPinned: checked })
                      }
                    />
                  </div>

                  {/* Expiry date */}
                  <div className="space-y-2">
                    <Label htmlFor="expiresAt">Expiry Date (optional)</Label>
                    <Input
                      id="expiresAt"
                      type="datetime-local"
                      value={formData.expiresAt}
                      onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty for no expiry. Expired announcements are automatically hidden.
                    </p>
                  </div>

                  <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={handleDialogClose} disabled={submitting}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="gap-2">
                      {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      {editingAnnouncement ? 'Update' : 'Publish'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search announcements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categoryOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 sm:gap-3">
        {[
          { label: 'Total', value: announcements.length, color: 'bg-gray-100 text-gray-700', icon: Megaphone },
          { label: 'Pinned', value: announcements.filter((a) => a.isPinned).length, color: 'bg-blue-50 text-blue-700', icon: Pin },
          ...categoryOptions.map((cat) => ({
            label: cat.label,
            value: announcements.filter((a) => a.category === cat.value).length,
            color: cat.color,
            icon: cat.icon,
          })),
        ].map((stat, idx) => (
          <div key={idx} className={`rounded-xl border px-2.5 sm:px-3 py-2 sm:py-2.5 text-center transition-all hover:shadow-sm ${stat.color}`}>
            <stat.icon className="h-3.5 w-3.5 mx-auto mb-1 opacity-60 hidden sm:block" />
            <div className="text-base sm:text-lg font-bold leading-tight">{stat.value}</div>
            <div className="text-[10px] sm:text-xs font-medium mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Announcements Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="text-center py-16 space-y-3">
              <Megaphone className="h-12 w-12 text-gray-300 mx-auto" />
              <div>
                <p className="font-medium text-gray-500">No announcements found</p>
                <p className="text-sm text-gray-400">
                  {searchQuery || filterCategory !== 'all'
                    ? 'Try adjusting your search or filter'
                    : 'Create your first announcement to get started'}
                </p>
              </div>
            </div>
          ) : (
            <ScrollArea className="w-full">
              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-28">Category</TableHead>
                      <TableHead className="w-28">Audience</TableHead>
                      <TableHead className="w-36">Posted</TableHead>
                      <TableHead className="w-36">Expires</TableHead>
                      <TableHead className="w-32">Posted By</TableHead>
                      <TableHead className="w-28 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAnnouncements.map((announcement) => (
                      <TableRow key={announcement.id} className="group">
                        <TableCell>
                          {announcement.isPinned && (
                            <Pin className="h-4 w-4 text-blue-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            <p className="font-medium text-sm break-words line-clamp-2">{announcement.title}</p>
                            <p className="text-xs text-muted-foreground break-words line-clamp-2 mt-0.5">
                              {announcement.content}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{getCategoryBadge(announcement.category)}</TableCell>
                        <TableCell>{getAudienceBadge(announcement.audience)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(announcement.datePosted)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {announcement.expiresAt
                            ? formatDate(announcement.expiresAt)
                            : <span className="text-gray-400">No expiry</span>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {announcement.postedBy}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setPreviewAnnouncement(announcement)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(announcement)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setDeleteConfirmId(announcement.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-3 p-3 sm:p-4">
                {filteredAnnouncements.map((announcement) => {
                  const catOpt = categoryOptions.find((c) => c.value === announcement.category);
                  const borderColor =
                    announcement.category === 'urgent'
                      ? 'border-l-red-500'
                      : announcement.category === 'academic'
                      ? 'border-l-indigo-500'
                      : announcement.category === 'deadline'
                      ? 'border-l-amber-500'
                      : announcement.category === 'event'
                      ? 'border-l-emerald-500'
                      : 'border-l-blue-500';
                  return (
                    <Card
                      key={announcement.id}
                      className={`border border-l-4 ${borderColor} transition-all duration-200 hover:shadow-md ${
                        announcement.isPinned ? 'bg-gradient-to-r from-amber-50/30 to-white ring-1 ring-amber-200/50' : ''
                      }`}
                    >
                      <CardContent className="p-3.5 sm:p-4 space-y-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            {catOpt && (
                              <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${catOpt.color} bg-opacity-20`}>
                                <catOpt.icon className="h-4 w-4" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                {announcement.isPinned && <Pin className="h-3 w-3 text-amber-500 flex-shrink-0" />}
                                <h4 className="font-semibold text-sm leading-tight line-clamp-2">{announcement.title}</h4>
                              </div>
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                                {announcement.content}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {getCategoryBadge(announcement.category)}
                          {getAudienceBadge(announcement.audience)}
                          {announcement.expiresAt && (
                            <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-500 border-gray-200">
                              <Clock className="h-2.5 w-2.5 mr-1" />
                              Expires {new Date(announcement.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(announcement.datePosted)}
                          </span>
                          <span className="truncate ml-2">{announcement.postedBy}</span>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 flex-1 gap-1.5 text-xs font-medium"
                            onClick={() => setPreviewAnnouncement(announcement)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 flex-1 gap-1.5 text-xs font-medium"
                            onClick={() => handleEdit(announcement)}
                          >
                            <Edit className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-9 flex-1 gap-1.5 text-xs font-medium text-destructive hover:text-destructive"
                            onClick={() => setDeleteConfirmId(announcement.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      {previewAnnouncement && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => setPreviewAnnouncement(null)}
        >
          <div
            className="bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl w-[95vw] sm:w-full sm:max-w-lg max-h-[90vh] overflow-hidden animate-in fade-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const catOpt = categoryOptions.find((c) => c.value === previewAnnouncement.category);
              const CatIcon = catOpt?.icon || Info;
              const bgColor =
                previewAnnouncement.category === 'urgent'
                  ? 'bg-gradient-to-r from-red-50 to-red-100/50 border-b border-red-200'
                  : previewAnnouncement.category === 'academic'
                  ? 'bg-gradient-to-r from-indigo-50 to-indigo-100/50 border-b border-indigo-200'
                  : previewAnnouncement.category === 'deadline'
                  ? 'bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-200'
                  : previewAnnouncement.category === 'event'
                  ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-200'
                  : 'bg-gradient-to-r from-blue-50 to-blue-100/50 border-b border-blue-200';
              const iconBg =
                previewAnnouncement.category === 'urgent'
                  ? 'bg-red-100 text-red-600'
                  : previewAnnouncement.category === 'academic'
                  ? 'bg-indigo-100 text-indigo-600'
                  : previewAnnouncement.category === 'deadline'
                  ? 'bg-amber-100 text-amber-600'
                  : previewAnnouncement.category === 'event'
                  ? 'bg-emerald-100 text-emerald-600'
                  : 'bg-blue-100 text-blue-600';
              return (
                <>
                  {/* Drag handle for mobile */}
                  <div className="sm:hidden flex justify-center pt-2 pb-0">
                    <div className="w-10 h-1 rounded-full bg-gray-300" />
                  </div>
                  <div className={`${bgColor} px-4 sm:px-6 py-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center ${iconBg}`}>
                          <CatIcon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            {getCategoryBadge(previewAnnouncement.category)}
                            {getAudienceBadge(previewAnnouncement.audience)}
                            {previewAnnouncement.isPinned && (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-200">
                                <Pin className="h-2.5 w-2.5 mr-1" />
                                Pinned
                              </Badge>
                            )}
                          </div>
                          <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                            {previewAnnouncement.title}
                          </h2>
                        </div>
                      </div>
                      <button
                        onClick={() => setPreviewAnnouncement(null)}
                        className="p-1.5 rounded-lg hover:bg-white/60 transition-colors flex-shrink-0"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <ScrollArea className="flex-1 min-h-0">
                    <div className="px-4 sm:px-6 py-4 sm:py-5">
                      <p className="text-gray-700 text-sm sm:text-[15px] leading-relaxed whitespace-pre-wrap">
                        {previewAnnouncement.content}
                      </p>
                    </div>
                  </ScrollArea>
                  <Separator />
                  <div className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-2 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Posted {formatDate(previewAnnouncement.datePosted)}</span>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-2">
                      {previewAnnouncement.expiresAt && (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Clock className="w-3.5 h-3.5" />
                          Expires {new Date(previewAnnouncement.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                      <span className="text-gray-400">by {previewAnnouncement.postedBy}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Announcement</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this announcement? This action cannot be undone. It will be removed from the public bulletin board immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
